/**
 * Brand Manager helpers — platform catalog from Super Admin Brands
 * (GET /me/brands/catalog). Adding a brand creates a Limited membership
 * via POST /me/brands/join (no pending-authorization wait).
 */

const PENDING_STORAGE_KEY = "kk_ma_pending_brands_v2";

function sortByName(brands) {
  return [...brands].sort((a, b) =>
    String(a.name || "").localeCompare(String(b.name || "")),
  );
}

/** Clear legacy mock Brand Manager rows (Walmart / Starbucks / etc.). */
export function clearLegacyMockManagedBrands() {
  try {
    localStorage.removeItem("kk_ma_managed_brands_v1");
  } catch {
    /* ignore */
  }
}

/**
 * Drop local "Pending Authorization" stubs — access is real Limited
 * membership from /me/brands/join, not a client-side pending queue.
 */
export function clearPendingAuthorizationBrands() {
  try {
    localStorage.removeItem(PENDING_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function brandAccessLevelLabel(roleScope) {
  const scope = String(roleScope || "franchisee").toLowerCase();
  if (scope === "corporate" || scope === "full") return "Full";
  if (scope === "franchisee" || scope === "limited") return "Limited";
  return "Limited";
}

/** Client-facing Brand-card badge text (Limited | Full — no MAAL acronym). */
export function brandAccessLevelBadgeText(roleScopeOrLabel) {
  return brandAccessLevelLabel(roleScopeOrLabel);
}

export function searchPlatformBrands(catalogBrands, query, managedIds) {
  const q = (query || "").trim().toLowerCase();
  const managed = managedIds instanceof Set ? managedIds : new Set(managedIds || []);
  return (catalogBrands || [])
    .filter((b) => {
      if (!q) return true;
      const name = b.name || b.brandName || "";
      return (
        name.toLowerCase().includes(q) ||
        String(b.industry || "")
          .toLowerCase()
          .includes(q) ||
        String(b.description || "")
          .toLowerCase()
          .includes(q)
      );
    })
    .map((b) => {
      const id = b.id || b.brandId;
      return {
        ...b,
        id,
        name: b.name || b.brandName,
        alreadyAdded: managed.has(id),
      };
    });
}

/**
 * Build Brand Manager cards from authorized brand memberships only.
 * Drops any brand not present in the Super Admin catalog.
 */
export function buildBrandManagerCards({
  catalogBrands = [],
  memberships = [],
} = {}) {
  const catalogById = new Map();
  for (const b of catalogBrands) {
    const id = b.id || b.brandId;
    if (id) catalogById.set(id, b);
  }

  const cards = [];

  for (const m of memberships) {
    if (!m?.brandId || m.status !== "active") continue;
    const catalog = catalogById.get(m.brandId);
    if (!catalog) continue;
    cards.push({
      id: m.brandId,
      name: catalog.name || catalog.brandName || m.brandId,
      // Brand Access Level badge: Full | Limited (never Franchisee/Corporate).
      type: brandAccessLevelLabel(m.roleScope),
      role: m.accessLevel || "—",
      lastAccessed: "—",
      locations: catalog.locationCount ?? catalog.locations ?? 0,
      zones: catalog.zones ?? 0,
      billingCycle: "—",
      accessStatus: "authorized",
      industry: catalog.industry || "",
      logoUrl: catalog.logoUrl || null,
    });
  }

  return sortByName(cards);
}
